// pages/inventory/SCRetrededTyreIssueListing.jsx
// ✅ Converted from: SCRetrededTyreIssueListing.aspx + SCRetrededTyreIssueListing.aspx.cs
// ✅ ALL business logic preserved:
//    - HO / Branch radio toggle (branchtype session)
//    - SCR No autocomplete
//    - Store, Supplier, Status dropdowns
//    - From/To date with FY validation
//    - Grid: Date, ItemCode, ItemName, SupplierName, IssueQty, ReceivedQty, PendingQty, DueDate, Status
//    - SerialNoReceive link (open popup) — enabled only for "Waiting" rows
//    - Receive Date per row (editable, only for Waiting rows)
//    - Submit: validate recdate > entry date, serial nos, ledger/fifo/stock updates
//    - RowDataBound: checkbox & link enabled only when Status="Waiting"
//    - After submit: re-check master received status, update master if all details received
// ✅ NO rights logic (excluded as instructed)
// ✅ Pagination added

import { useEffect, useContext, useState, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Plus, Search, RefreshCcw, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, CheckSquare, Link2, ClipboardList
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";

// ─────────────── helpers ───────────────
const s     = (v) => (v ?? "").toString().trim();
const toInt = (v, def = 0) => { const n = Number(v); return Number.isFinite(n) ? n : def; };

const today00 = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };

const isoToDate = (iso) => {
  const x = s(iso); if (!x) return null;
  const d = new Date(`${x}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const dmyToDate = (dmy) => {
  const x = s(dmy); if (!x) return null;
  const m = x.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
  return isNaN(d.getTime()) ? null : d;
};

const isoToDmy = (iso) => {
  const d = isoToDate(iso); if (!d) return "";
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};

const dmyToIso = (dmy) => {
  const d = dmyToDate(dmy); if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

const isoAddDays = (iso, days) => {
  const d = isoToDate(iso) || today00();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// Case-insensitive field reader — SQL Server SP columns may differ in casing
const getField = (row, fieldName) => {
  if (!row || typeof row !== "object") return "";
  // exact match first
  if (fieldName in row) return s(row[fieldName]);
  // case-insensitive fallback
  const lower = fieldName.toLowerCase();
  const key = Object.keys(row).find(k => k.toLowerCase() === lower);
  return key ? s(row[key]) : "";
};

// ─────────────── component ───────────────
const SCRetrededTyreIssueListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass  = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // session values
  const branchId    = localStorage.getItem("branchId") || "";
  const branchType  = localStorage.getItem("branchType") || "Branch"; // "HO" or "Branch"
  const finYearId   = localStorage.getItem("finYearId") || "";
  const loginUserId = localStorage.getItem("userId") || "";
  const storeId     = localStorage.getItem("storeId") || "";
  const financeYearStartDate = localStorage.getItem("financeYearStartDate") || "";

  // ── filters ──
  // ASP: branchtype != "HO" → rbtn_brch checked; else rbtn_ho
  const [purType, setPurType]       = useState(branchType === "HO" ? "H" : "B");
  const [scrNo, setScrNo]           = useState("");
  const [selStore, setSelStore]     = useState("Select");
  const [fromDate, setFromDate]     = useState(isoAddDays(todayIso(), -7));
  const [toDate, setToDate]         = useState(todayIso());
  const [selSupplier, setSelSupplier] = useState("Select");
  const [selStatus, setSelStatus]   = useState("Select");

  // ── dropdown data ──
  const [stores, setStores]         = useState([]);
  const [suppliers, setSuppliers]   = useState([]);

  // ── autocomplete SCR No ──
  const [acItems, setAcItems]       = useState([]);
  const [acOpen, setAcOpen]         = useState(false);

  // ── grid ──
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // ── per-row receive date & submit checked ──
  // key = fld_SCRIssueDetid
  const [receiveDates, setReceiveDates]   = useState({}); // { detid: "ISO" }
  const [submitChecked, setSubmitChecked] = useState({}); // { detid: bool }

  // ── pagination ──
  const [page, setPage]       = useState(1);
  const [pageSize]            = useState(10);
  const [total, setTotal]     = useState(0);
  const totalPages            = Math.max(1, Math.ceil(total / pageSize));

  const [submitting, setSubmitting] = useState(false);
  // Stores serial nos received from SerialNoEntry popup via postMessage
  // Key = itemid (string), Value = array of { ItemSno, ItemSnoId, itemid, price }
  const [serialNosMap, setSerialNosMap] = useState({});

  // ─── styles ───
  const inputBase =
    `w-full px-4 py-2.5 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/50 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");

  const selectBase =
    `w-full px-4 py-2.5 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/50 border-white/10 text-white focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-400");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow ` +
    `hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-700 hover:bg-gray-50");

  const thBase = `px-3 py-3 text-xs font-bold uppercase tracking-wider text-white text-center whitespace-nowrap`;
  const tdBase = `px-3 py-3 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ─── date validation — ASP Check_from_to + checkDate ───
  const validateDates = () => {
    const t  = today00();
    const fd = isoToDate(fromDate);
    const td = isoToDate(toDate);

    if (fd && fd > t) { alert("Select from date lesser than current date"); return false; }
    if (td && td > t) { alert("Select to date lesser than current date"); return false; }
    if (fd && td && td < fd) { alert("Please select from date less than to date"); return false; }

    const fy = dmyToDate(financeYearStartDate);
    if (fy) {
      if (fd && fd < fy) {
        alert(`You can select only Greater than or Equal to Financial Year Date  ${financeYearStartDate}`);
        return false;
      }
      if (td && td < fy) {
        alert(`You can select only Greater than or Equal to Financial Year Date  ${financeYearStartDate}`);
        return false;
      }
    }
    return true;
  };

  // ─── load dropdowns on mount ───
  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── initial grid load ───
  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── reload when page changes ───
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ─── Listen for postMessage from SerialNoEntry popup ─────────────
  // ASP used Session[] — React uses postMessage → state (sessionStorage is per-tab)
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;

      // Accept SCRI_SERIAL_NOS and SCRR_SERIAL_NOS
      if (data.entryType === "SCRR" || data.entryType === "SCRI") {
        const itemid = String(data.itemId ?? "");
        const snos   = Array.isArray(data.serialNos) ? data.serialNos : [];
        if (itemid && snos.length > 0) {
          setSerialNosMap(prev => ({ ...prev, [itemid]: snos }));
          toast.success(`${snos.length} serial no(s) received for item ${itemid}`);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const loadDropdowns = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-listing/dropdowns`, {
        params: { branchId, purType, finYearId }
      });
      if (res.data?.success) {
        setStores(res.data.stores || []);
        setSuppliers(res.data.suppliers || []);
        // set default store
        if (res.data.stores?.length > 0) {
          setSelStore(s(res.data.stores[0].value));
        }
      }
    } catch {
      toast.error("Failed to load dropdowns");
    }
  };

  // reload suppliers when purType changes (ASP: rbtn_ho/rbtn_brch_CheckedChanged)
  const loadSuppliers = async (pType) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-listing/suppliers`, {
        params: { branchId, purType: pType, finYearId }
      });
      if (res.data?.success) {
        setSuppliers(res.data.suppliers || []);
        setSelSupplier("Select");
      }
    } catch { /* silent */ }
  };

  const handlePurTypeChange = (val) => {
    setPurType(val);
    loadSuppliers(val);
  };

  // ─── Autocomplete SCR No ───
  const fetchAc = useCallback(async (prefix) => {
    if (!prefix) { setAcItems([]); setAcOpen(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-listing/autocomplete`, {
        params: { branchId, purType, supplierId: selSupplier, prefix, finYearId }
      });
      if (res.data?.success) {
        setAcItems(res.data.suggestions || []);
        setAcOpen(true);
      }
    } catch { /* silent */ }
  }, [branchId, purType, selSupplier, finYearId]);

  // ─── Fetch Grid ───
  const fetchList = async (p = page) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/inventory/sc-retreded-tyre-listing/list`, {
        params: {
          branchId,
          finYearId,
          purType,
          scrNo: scrNo.trim(),
          fromDate: fromDate ? isoToDmy(fromDate) : "",
          toDate:   toDate   ? isoToDmy(toDate)   : "",
          supplierId: selSupplier,
          status:     selStatus,
          page: p,
          pageSize
        }
      });
      if (res.data?.success) {
        const list = res.data.list || [];
        setRows(list);
        setTotal(res.data.totalCount || 0);
        setPage(res.data.page || p);
        // reset per-row state on rebind
        setReceiveDates({});
        setSubmitChecked({});
      } else {
        toast.error(res.data?.message || "Failed to load listing");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ─── Search ───
  const handleSearch = () => {
    if (!validateDates()) return;
    setPage(1);
    fetchList(1);
  };

  // ─── Serial No Receive popup ───
  // ASP: SerialNoEntry.aspx?item=itemid-storeid-branchid-SCRIssuedetid&qty=pendingqty&pro=SCRR&f=1
  const handleSerialNoReceive = (row) => {
    const pendingQty = getField(row, "fld_Pendingqty");
    const itemid     = getField(row, "fld_itemid");
    const detid      = getField(row, "fld_SCRIssueDetid");
    const serialno   = getField(row, "serialno");

    if (!pendingQty) {
      alert("Please enter Transfer qty and try again"); return;
    }
    if (pendingQty.includes(".")) {
      alert("Serial no entry not allowed for decimal transfer quantity"); return;
    }

    const qty = toInt(pendingQty);

    if (qty > 0 || serialno === "Closed") {
      // Clear existing serial nos from state if qty changed (force re-selection)
      const existing = serialNosMap[itemid];
      if (existing && existing.length !== qty) {
        setSerialNosMap(prev => { const n = {...prev}; delete n[itemid]; return n; });
      }
      const url = `SerialNoEntry?item=${itemid}-${selStore}-${branchId}-${detid}&qty=${pendingQty}&pro=SCRR&f=1&loginUserId=${loginUserId}`;
      window.open(url, "_blank", "width=800,height=800,scrollbars=yes,menubar=no,resizable=no");
    } else {
      alert("Serial no entry not allowed for Transfer quantity 0");
    }
  };

  // ─── Submit ───
  const handleSubmit = async () => {
    const checkedDetIds = Object.entries(submitChecked)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (checkedDetIds.length === 0) {
      alert("Please select at least one row to submit");
      return;
    }

    // Build payload — validate each checked row
    const itemsPayload = [];

    for (const detid of checkedDetIds) {
      const row = rows.find(r => getField(r, "fld_SCRIssueDetid") === detid);
      if (!row) continue;

      const recDateIso = receiveDates[detid] || "";
      if (!recDateIso) {
        alert("Pls Select Receive Date");
        return;
      }

      // ASP: enterdate > receivedat → error
      const entryDate   = dmyToDate(getField(row, "fld_date")) || isoToDate(getField(row, "fld_date"));
      const receiveDate = isoToDate(recDateIso);
      if (entryDate && receiveDate && entryDate > receiveDate) {
        alert("Pls Enter Receive Date Greate Than Entry Date");
        return;
      }

      // Get serial nos from session storage
      const itemid = getField(row, "fld_itemid");
      // Read serial nos from React state (populated by postMessage from SerialNoEntry popup)
      const serialNos = serialNosMap[itemid] || [];
      if (serialNos.length === 0) {
        alert(`Please open the "Yes" link to select serial nos for item ${getField(row, "fld_itemname") || itemid}`);
        return;
      }

      itemsPayload.push({
        itemid,
        SCRIid:      getField(row, "fld_SCRIssueid"),
        SCRIdetid:   detid,
        entryDate:   getField(row, "fld_date"),
        receiveDate: isoToDmy(recDateIso),
        pendingQty:  getField(row, "fld_Pendingqty"),
        serialNos
      });
    }

    if (itemsPayload.length === 0) return;

    try {
      setSubmitting(true);
      const res = await axios.post(`${BASE_URL}/api/inventory/sc-retreded-tyre-listing/submit`, {
        branchId,
        storeId:    selStore !== "Select" ? selStore : storeId, // prefer UI-selected store
        loginUserId,
        finYearId,
        items: itemsPayload
      });

      if (res.data?.success) {
        // Clear serial nos from state for submitted items
        const submittedIds = itemsPayload.map(item => item.itemid);
        setSerialNosMap(prev => {
          const next = { ...prev };
          submittedIds.forEach(id => delete next[id]);
          return next;
        });
        toast.success("Submitted successfully!");
        // ASP: BindGrid() after submit
        await fetchList(page);
      } else {
        alert(res.data?.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      alert("Submission failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Pagination buttons ───
  const pageButtons = useMemo(() => {
    const win = 5;
    let start = Math.max(1, page - Math.floor(win / 2));
    let end   = Math.min(totalPages, start + win - 1);
    if (end - start < win - 1) start = Math.max(1, end - win + 1);
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo   = Math.min(page * pageSize, total);

  // status badge
  const statusBadge = (status) => {
    const v = s(status);
    if (v === "Waiting") return (
      <span className="px-2 py-1 rounded-lg text-xs font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 animate-pulse">
        Waiting
      </span>
    );
    if (v === "Received") return (
      <span className="px-2 py-1 rounded-lg text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
        Received
      </span>
    );
    return <span className="text-xs text-gray-500">{v || "—"}</span>;
  };

  // ────────────────────────────── RENDER ──────────────────────────────
  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SC RETREDED TYRE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `${currentSkin.glass} bg-white text-black border-gray-200`
        }`}>

          {/* ── Page Header ── */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <ClipboardList className={isDark ? "text-white" : "text-gray-700"} />
              </div>
              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Sub Contract Retreded Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Filter / Receive
                </div>
              </div>
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={() => window.location.href = "/inventory/SCRetrededTyreIssue_Entry"}
                className={primaryBtn}
              >
                <Plus className="w-4 h-4" /> Add New
              </button>
              <button onClick={() => setShowFilters(v => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ── Filter Section ── */}
          {showFilters && (
            <div className={`${cardBase} p-6 mb-6`}>
              {/* HO / Branch radio — ASP: rbtn_ho, rbtn_brch */}
              <div className="flex items-center gap-6 mb-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="purType"
                    value="H"
                    checked={purType === "H"}
                    onChange={() => handlePurTypeChange("H")}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className={labelBase}>H.O</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="purType"
                    value="B"
                    checked={purType === "B"}
                    onChange={() => handlePurTypeChange("B")}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className={labelBase}>Branch</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* SCR No autocomplete */}
                <div className="relative">
                  <label className={labelBase}>SCR No</label>
                  <input
                    value={scrNo}
                    autoComplete="off"
                    placeholder="Type SCR No..."
                    onChange={e => { setScrNo(e.target.value); fetchAc(e.target.value); }}
                    onBlur={() => setTimeout(() => setAcOpen(false), 150)}
                    onFocus={() => { if (acItems.length > 0) setAcOpen(true); }}
                    className={inputBase + " mt-1.5"}
                  />
                  {acOpen && acItems.length > 0 && (
                    <div className={`absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {acItems.map((x, i) => (
                        <button key={i} type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { setScrNo(x); setAcOpen(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm ${isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"}`}
                        >{x}</button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Store */}
                <div>
                  <label className={labelBase}>Store</label>
                  <select value={selStore} onChange={e => setSelStore(e.target.value)} className={selectBase + " mt-1.5"}>
                    <option value="Select">Select</option>
                    {stores.map(st => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </select>
                </div>

                {/* From Date */}
                <div>
                  <label className={labelBase}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    max={todayIso()}
                    onChange={e => setFromDate(e.target.value)}
                    className={inputBase + " mt-1.5"}
                  />
                </div>

                {/* To Date */}
                <div>
                  <label className={labelBase}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    max={todayIso()}
                    onChange={e => setToDate(e.target.value)}
                    className={inputBase + " mt-1.5"}
                  />
                </div>

                {/* Supplier */}
                <div>
                  <label className={labelBase}>Supplier</label>
                  <select value={selSupplier} onChange={e => setSelSupplier(e.target.value)} className={selectBase + " mt-1.5"}>
                    <option value="Select">Select</option>
                    {suppliers.map(sup => (
                      <option key={sup.value} value={sup.value}>{sup.label}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className={labelBase}>Status</label>
                  <select value={selStatus} onChange={e => setSelStatus(e.target.value)} className={selectBase + " mt-1.5"}>
                    <option value="Select">Select</option>
                    <option value="Waiting">Waiting</option>
                    <option value="Received">Received</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-center mt-6">
                <button onClick={handleSearch} className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          {/* ── Grid ── */}
          <div className={cardBase}>
            <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-200"}`}>
              <span className={`text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Results
              </span>
              <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Showing {showingFrom}–{showingTo} of {total}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={primaryBtnClass}>
                  <tr>
                    <th className={thBase}>S.No</th>
                    <th className={thBase}>Date</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>Supplier Name</th>
                    <th className={thBase}>Issue Qty</th>
                    <th className={thBase}>Received Qty</th>
                    <th className={thBase}>Pending Qty</th>
                    <th className={thBase}>Due Date</th>
                    <th className={thBase}>Status</th>
                    <th className={thBase}>Receive</th>
                    <th className={thBase}>Receive Date</th>
                    <th className={thBase}>Select</th>
                  </tr>
                </thead>

                <tbody className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                  {loading
                    ? Array.from({ length: pageSize }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 13 }).map((__, c) => (
                            <td key={c} className="px-3 py-3">
                              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                            </td>
                          ))}
                        </tr>
                      ))
                    : rows.length === 0
                      ? (
                        <tr>
                          <td colSpan={13} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            No Records Found
                          </td>
                        </tr>
                      )
                      : rows.map((row, idx) => {
                          const detid      = getField(row, "fld_SCRIssueDetid");
                          // ASP RowDataBound: checkbox & link enabled only if Status=="Waiting"
                          const isWaiting  = getField(row, "fld_status") === "Waiting";
                          const isClosed   = getField(row, "serialno") === "Closed";
                          // Receive link: enabled if fld_Isreceived is truthy
                          const canReceive = getField(row, "fld_Isreceived") === "1" || getField(row, "fld_Isreceived") === "True" || getField(row, "fld_Isreceived") === "true";

                          return (
                            <tr
                              key={detid || idx}
                              className={`${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"} transition`}
                            >
                              {/* S.No — global index across pages */}
                              <td className={tdBase}>{(page - 1) * pageSize + idx + 1}</td>

                              {/* Date */}
                              <td className={tdBase}>{getField(row, "fld_date")}</td>

                              {/* Item Code */}
                              <td className={`${tdBase} text-left`}>{getField(row, "fld_itemcode")}</td>

                              {/* Item Name */}
                              <td className={`${tdBase} text-left`}>{getField(row, "fld_itemname")}</td>

                              {/* Supplier Name */}
                              <td className={tdBase}>{getField(row, "fld_Suppliername")}</td>

                              {/* Issue Qty */}
                              <td className={tdBase}>{getField(row, "fld_issueqty")}</td>

                              {/* Received Qty */}
                              <td className={tdBase}>{getField(row, "fld_receivedqty")}</td>

                              {/* Pending Qty */}
                              <td className={tdBase}>
                                <span className={`font-semibold ${toInt(getField(row, "fld_Pendingqty")) > 0 ? "text-orange-500" : "text-green-500"}`}>
                                  {getField(row, "fld_Pendingqty")}
                                </span>
                              </td>

                              {/* Due Date */}
                              <td className={tdBase}>{getField(row, "fld_DueDate")}</td>

                              {/* Status */}
                              <td className={tdBase}>{statusBadge(getField(row, "fld_status"))}</td>

                              {/* Receive Link — ASP: lnkSerialNoReceive */}
                              <td className={tdBase}>
                                <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={() => handleSerialNoReceive(row)}
                                  disabled={!canReceive}
                                  className={`inline-flex items-center gap-1 text-sm font-medium ${
                                    canReceive
                                      ? "text-blue-500 hover:underline"
                                      : "text-gray-400 cursor-not-allowed"
                                  }`}
                                >
                                  <Link2 className="w-3 h-3" />
                                  {getField(row, "serialno") || "No"}
                                </button>
                                {serialNosMap[getField(row, "fld_itemid")]?.length > 0 && (
                                  <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">
                                    ✓ {serialNosMap[getField(row, "fld_itemid")].length} selected
                                  </span>
                                )}
                              </div>
                              </td>

                              {/* Receive Date — editable only for Waiting rows */}
                              <td className={tdBase}>
                                {isWaiting ? (
                                  <input
                                    type="date"
                                    value={receiveDates[detid] || ""}
                                    max={todayIso()}
                                    onChange={e =>
                                      setReceiveDates(prev => ({ ...prev, [detid]: e.target.value }))
                                    }
                                    className={`w-32 px-2 py-1.5 rounded-lg border text-xs outline-none ${
                                      isDark
                                        ? "bg-gray-800 border-white/10 text-white"
                                        : "bg-white border-gray-300 text-gray-900"
                                    }`}
                                  />
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>

                              {/* Submit checkbox — enabled only for Waiting rows */}
                              <td className={tdBase}>
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 cursor-pointer"
                                  disabled={!isWaiting}
                                  checked={!!submitChecked[detid]}
                                  onChange={e =>
                                    setSubmitChecked(prev => ({ ...prev, [detid]: e.target.checked }))
                                  }
                                />
                              </td>
                            </tr>
                          );
                        })
                  }
                </tbody>
              </table>
            </div>

            {/* Submit Footer */}
            <div className={`p-4 flex justify-center border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
              <button onClick={handleSubmit} disabled={submitting || loading} className={primaryBtn}>
                <CheckSquare className="w-4 h-4" />
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>

          {/* ── Pagination ── */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${page === 1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {pageButtons[0] > 1 && (
                  <>
                    <button onClick={() => setPage(1)} className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200">1</button>
                    {pageButtons[0] > 2 && <span className="px-3 py-2 text-sm text-gray-400">…</span>}
                  </>
                )}

                {pageButtons.map(pno => (
                  <button
                    key={pno}
                    onClick={() => setPage(pno)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === pno
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >{pno}</button>
                ))}

                {pageButtons[pageButtons.length - 1] < totalPages && (
                  <>
                    {pageButtons[pageButtons.length - 1] < totalPages - 1 && (
                      <span className="px-3 py-2 text-sm text-gray-400">…</span>
                    )}
                    <button onClick={() => setPage(totalPages)} className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200">
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${page === totalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </LayoutWrapper>
  );
};

export default SCRetrededTyreIssueListing;